import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Bug, X, Send, CheckCircle, AlertCircle } from 'lucide-react'

const SUPABASE_URL     = import.meta.env.VITE_SUPABASE_URL
const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

// Load the Turnstile script once for the lifetime of the page
function ensureTurnstileScript() {
  if (document.getElementById('cf-turnstile-script')) return
  const s = document.createElement('script')
  s.id    = 'cf-turnstile-script'
  s.src   = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
  s.async = true
  document.head.appendChild(s)
}

export default function BugReportButton() {
  const location = useLocation()
  const [open, setOpen]         = useState(false)
  const [title, setTitle]       = useState('')
  const [description, setDesc]  = useState('')
  const [steps, setSteps]       = useState('')
  const [token, setToken]       = useState(null)
  const [status, setStatus]     = useState('idle') // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('')

  const widgetIdRef   = useRef(null)
  const containerRef  = useRef(null)

  // Hide on all admin routes
  if (location.pathname.startsWith('/admin')) return null

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => { ensureTurnstileScript() }, [])

  // Render / destroy Turnstile widget with the modal
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!open) {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
      return
    }

    setToken(null)

    const render = () => {
      if (!containerRef.current || !TURNSTILE_SITEKEY) return
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey:           TURNSTILE_SITEKEY,
        callback:          (t) => setToken(t),
        'error-callback':  () => setToken(null),
        'expired-callback': () => setToken(null),
      })
    }

    if (window.turnstile) {
      render()
    } else {
      const script = document.getElementById('cf-turnstile-script')
      script?.addEventListener('load', render, { once: true })
      return () => script?.removeEventListener('load', render)
    }

    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [open])

  function handleClose() {
    if (status === 'submitting') return
    setOpen(false)
    setStatus('idle')
    setErrorMsg('')
    setTitle('')
    setDesc('')
    setSteps('')
    setToken(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!token || status === 'submitting') return

    setStatus('submitting')
    setErrorMsg('')

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/report-bug`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:          title.trim(),
          description:    description.trim(),
          steps:          steps.trim(),
          pageUrl:        window.location.href,
          turnstileToken: token,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        if (res.status === 429) {
          setErrorMsg("You've reached the daily limit of 3 bug reports. Try again tomorrow.")
        } else if (data.error === 'turnstile_failed') {
          setErrorMsg('Bot check failed. Please refresh and try again.')
        } else {
          setErrorMsg('Something went wrong. Please try again.')
        }
        setStatus('error')
        // Reset Turnstile so they can retry
        if (widgetIdRef.current != null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current)
          setToken(null)
        }
        return
      }

      setStatus('success')
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.')
      setStatus('error')
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current)
        setToken(null)
      }
    }
  }

  const canSubmit = token && title.trim() && description.trim() && status !== 'submitting'

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Report a bug"
        title="Report a bug"
        style={{
          position:    'fixed',
          bottom:      '24px',
          right:       '24px',
          zIndex:      1000,
          width:       '48px',
          height:      '48px',
          borderRadius: '50%',
          background:  '#17140F',
          color:       '#fff',
          border:      'none',
          cursor:      'pointer',
          display:     'flex',
          alignItems:  'center',
          justifyContent: 'center',
          boxShadow:   '0 4px 16px rgba(0,0,0,0.18)',
          transition:  'background 0.15s, transform 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#E8672E'; e.currentTarget.style.transform = 'scale(1.08)' }}
        onMouseLeave={e => { e.currentTarget.style.background = '#17140F'; e.currentTarget.style.transform = 'scale(1)' }}
      >
        <Bug size={20} />
      </button>

      {/* Modal backdrop */}
      {open && (
        <div
          onClick={e => { if (e.target === e.currentTarget) handleClose() }}
          style={{
            position:   'fixed',
            inset:       0,
            zIndex:      1001,
            background: 'rgba(0,0,0,0.45)',
            display:    'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding:    '1rem',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          <div style={{
            background:   '#FFFFFF',
            borderRadius: '12px',
            width:        '100%',
            maxWidth:     '480px',
            boxShadow:    '0 20px 60px rgba(0,0,0,0.25)',
            overflow:     'hidden',
          }}>
            {/* Header */}
            <div style={{
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'space-between',
              padding:        '1.25rem 1.5rem',
              borderBottom:   '1px solid #EDE6D6',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Bug size={18} color="#E8672E" />
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#17140F' }}>Report a bug</span>
              </div>
              <button
                onClick={handleClose}
                disabled={status === 'submitting'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#6B665C', display: 'flex', alignItems: 'center',
                  padding: '4px', borderRadius: '4px',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem' }}>
              {status === 'success' ? (
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <CheckCircle size={40} color="#22C55E" style={{ marginBottom: '1rem' }} />
                  <p style={{ fontWeight: 700, fontSize: '1rem', color: '#17140F', margin: '0 0 0.5rem' }}>
                    Bug reported — thank you!
                  </p>
                  <p style={{ color: '#6B665C', fontSize: '0.875rem', margin: '0 0 1.5rem' }}>
                    We've logged your report and will look into it.
                  </p>
                  <button
                    onClick={handleClose}
                    style={{
                      padding:      '10px 24px',
                      background:   '#E8672E',
                      color:        '#fff',
                      border:       'none',
                      borderRadius: '6px',
                      cursor:       'pointer',
                      fontWeight:   600,
                      fontSize:     '0.875rem',
                    }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <Field label="Title" required>
                    <input
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="Brief summary of the issue"
                      maxLength={150}
                      required
                      style={inputStyle}
                    />
                  </Field>

                  <Field label="Description" required style={{ marginTop: '1rem' }}>
                    <textarea
                      value={description}
                      onChange={e => setDesc(e.target.value)}
                      placeholder="What happened? What did you expect to happen?"
                      maxLength={2000}
                      required
                      rows={4}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
                    />
                  </Field>

                  <Field label="Steps to reproduce" style={{ marginTop: '1rem' }}>
                    <textarea
                      value={steps}
                      onChange={e => setSteps(e.target.value)}
                      placeholder="1. Go to…&#10;2. Click on…&#10;3. See error"
                      maxLength={1000}
                      rows={3}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }}
                    />
                  </Field>

                  {/* Cloudflare Turnstile */}
                  <div style={{ marginTop: '1.25rem' }}>
                    <div ref={containerRef} />
                    {!TURNSTILE_SITEKEY && (
                      <p style={{ color: '#F59E0B', fontSize: '0.75rem', margin: '4px 0 0' }}>
                        Turnstile site key not configured.
                      </p>
                    )}
                  </div>

                  {status === 'error' && (
                    <div style={{
                      marginTop:    '1rem',
                      padding:      '0.75rem 1rem',
                      background:   '#FEF2F2',
                      borderRadius: '6px',
                      display:      'flex',
                      gap:          '0.5rem',
                      alignItems:   'flex-start',
                    }}>
                      <AlertCircle size={16} color="#EF4444" style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span style={{ color: '#DC2626', fontSize: '0.875rem' }}>{errorMsg}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    style={{
                      marginTop:    '1.25rem',
                      width:        '100%',
                      padding:      '11px',
                      background:   canSubmit ? '#E8672E' : '#EDE6D6',
                      color:        canSubmit ? '#fff' : '#A0A0A0',
                      border:       'none',
                      borderRadius: '6px',
                      cursor:       canSubmit ? 'pointer' : 'not-allowed',
                      fontWeight:   600,
                      fontSize:     '0.9rem',
                      display:      'flex',
                      alignItems:   'center',
                      justifyContent: 'center',
                      gap:          '0.5rem',
                      transition:   'background 0.15s',
                    }}
                  >
                    {status === 'submitting' ? (
                      <>
                        <span style={{
                          width: '14px', height: '14px',
                          border: '2px solid rgba(255,255,255,0.4)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          animation: 'spin 0.7s linear infinite',
                          display: 'inline-block',
                        }} />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Send size={15} />
                        Send report
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  )
}

function Field({ label, required, children, style }) {
  return (
    <div style={style}>
      <label style={{
        display:    'block',
        fontSize:   '0.8125rem',
        fontWeight: 600,
        color:      '#17140F',
        marginBottom: '5px',
      }}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: '3px' }}>*</span>}
      </label>
      {children}
    </div>
  )
}

const inputStyle = {
  width:        '100%',
  padding:      '9px 12px',
  border:       '1px solid #E8E4DF',
  borderRadius: '6px',
  fontSize:     '0.875rem',
  color:        '#17140F',
  background:   '#fff',
  outline:      'none',
  boxSizing:    'border-box',
  fontFamily:   "'Inter', sans-serif",
}
